/**
 * The call floor — docs/design-handoff/tossker-redesign/README.md §2 "Call
 * floor". A zone pinned above the rail's filter row (`QueueRail.tsx`),
 * showing every live/ringing call plus anything voice still owes a human
 * (a missed call, an ended call with no logged time), so a tech never has to
 * scroll the queue to see who is on the phone right now.
 *
 * SCOPE (task-3 brief, binding decision 2): the design shows per-technician
 * call state; `Peer` (store.ts) carries no such field, and this component
 * invents none. Every card here is derived only from `ThreadSummary`/
 * `CallFacts`, already on the store — see `callFloorCards` in ./format.ts.
 * A live call with no resolvable owner renders with no name line at all
 * ("On this call", not a blank slot) rather than guessing.
 *
 * COLLAPSE (binding decision 1): the README proposes an auto-collapse timer
 * after ten minutes empty. Cut — collapse is manual, via the chevron, and
 * persists as `store.floorOpen`. A ringing or live call always forces the
 * floor open regardless of that flag (`isOpen` below); the flag only decides
 * what happens once nothing is live. The strip never disappears — see
 * `floorCollapsedLabel`.
 *
 * VOICE NEVER SENDS (binding decision 4): every card and every action here
 * (`Call back`, `Log time`, clicking a card) does exactly one thing —
 * `selectThread`, the same navigation `RailRow` already does. Nothing here
 * originates, answers, or ends a call; call lifecycle is 3CX's alone.
 */
import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { Avatar, Icon } from '../ui'
import { useStore } from '../../store'
import {
  activateOnKey,
  agoLabel,
  callFloorCards,
  callFloorCounts,
  channelTileStyle,
  floorCollapsedLabel,
  floorSummaryText,
  formatFloorClock,
  initialsOf,
  lastVoiceActivityAt,
} from './format'
import type { CallFloorCard } from './format'

/**
 * Ticks once a second off real wall-clock time, exactly like
 * thread/LiveCallStrip.tsx's own clock and for the same two reasons: it
 * survives a paused tab without drifting. It runs whenever the card is on
 * screen — the old covering gate froze colleagues' call clocks (Andrew,
 * 2026-09-01): coverage is a duty state, not a display gate.
 * Not shared with LiveCallStrip as a module — see CallFloor's own header
 * comment and formatFloorClock's — but the same shape, because it is the
 * same fact: `startedAtMs` is memoised on `sinceIso` exactly like
 * LiveCallStrip's own `useMemo`, not re-parsed on every render (fix round 1,
 * task 3 review).
 */
function useElapsedSeconds(sinceIso: string): number {
  const startedAtMs = useMemo(() => new Date(sinceIso).getTime(), [sinceIso])
  const [elapsedS, setElapsedS] = useState(() => Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000)))

  useEffect(() => {
    setElapsedS(Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000)))
    const id = setInterval(() => {
      setElapsedS(Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000)))
    }, 1000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startedAtMs])

  return elapsedS
}

const VOICE_DOWN_COPY = 'Voice events are not arriving. Calls will not appear in the rail.'

function StatusDot({ ok }: { ok: boolean }) {
  return <span aria-hidden="true" className={['h-[7px] w-[7px] flex-none rounded-full', ok ? 'bg-ok' : 'bg-bad'].join(' ')} />
}

/** headphones · CALL FLOOR · summary · chevron — shared by every open state and, rotated, the collapsed strip. */
function FloorHeaderRow({ summary, onToggle, collapsed }: { summary: string; onToggle: () => void; collapsed: boolean }) {
  return (
    <div
      data-testid="call-floor-toggle"
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => activateOnKey(e, onToggle)}
      className="flex cursor-pointer items-center gap-2 px-0.5"
    >
      <Icon name="headphones" size={15} className="flex-none text-cc-blue" />
      <span className="text-[11px] font-semibold tracking-[.09em] text-cc-blue-text">CALL FLOOR</span>
      <span data-testid="call-floor-summary" className="truncate text-[11px] font-medium text-mid">
        {summary}
      </span>
      <span
        data-testid="call-floor-chevron"
        className="ml-auto flex-none text-mid"
        style={{ transform: collapsed ? 'rotate(-90deg)' : undefined, display: 'inline-flex' }}
      >
        <Icon name="chevron-down" size={15} />
      </span>
    </div>
  )
}

function CardShell({
  onSelect,
  selected,
  radiusClass,
  style,
  className,
  children,
}: {
  onSelect: () => void
  selected: boolean
  radiusClass: string
  style?: CSSProperties
  className?: string
  children: ReactNode
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={selected ? 'true' : undefined}
      onClick={onSelect}
      onKeyDown={(e) => activateOnKey(e, onSelect)}
      className={['cursor-pointer bg-card', radiusClass, className ?? ''].join(' ')}
      style={style}
    >
      {children}
    </div>
  )
}

function RingingCard({ card, covering, onSelect }: { card: CallFloorCard; covering: boolean; onSelect: () => void }) {
  const { thread } = card
  const call = thread.call!
  const elapsedS = useElapsedSeconds(thread.openedAt)
  return (
    <CardShell
      onSelect={onSelect}
      selected={false}
      radiusClass="rounded-card animate-tk-ring"
      className="shadow-sm"
      style={{ border: '1px solid var(--brand-accent)' }}
    >
      <div className="flex items-center gap-[11px] p-[11px]">
        {/* Fix round 2 (finding 5): was `text-white`, a raw Tailwind class
            the hex-literal guard never sees — 1.53:1 in Legacy against
            --brand-accent's bright caution yellow, under the 3:1 minimum for
            a non-text graphic in every theme. --on-brand-accent is a fixed
            near-black tuned to clear 3:1 against --brand-accent everywhere. */}
        <span
          className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px]"
          style={{ background: 'var(--brand-accent)', color: 'var(--on-brand-accent)' }}
        >
          <Icon name="phone-incoming" size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-[7px]">
            <span className="truncate text-[13px] font-semibold text-hi">{thread.who}</span>
            {/* Fix round 2 (finding 6): --on-tint-accent is legible on
                --tint-accent, not on this card's --surface-card — was
                2.26:1 in Legacy Day. --brand-accent-text is tuned for text
                directly on the card. */}
            <span className="flex-none text-[9.5px] font-semibold tracking-[.08em]" style={{ color: 'var(--brand-accent-text)' }}>
              RINGING
            </span>
            <span
              data-testid={`call-floor-clock-${thread.id}`}
              className="ml-auto flex-none font-mono text-[14px]"
              style={{ color: 'var(--brand-accent-text)', fontVariantNumeric: 'tabular-nums' }}
            >
              {formatFloorClock(elapsedS)}
            </span>
          </div>
          <div className="mt-[2px] truncate text-[11.5px] text-mid">
            {thread.client ?? 'Unknown caller'} · ext {call.ext ?? '—'} · nobody has picked up
          </div>
        </div>
      </div>
    </CardShell>
  )
}

/** Shared by `live-mine` and a `selected` card that also belongs to this viewer — the meter, the "You have it" chip, and the extension line. */
function LiveMeterRow({ ext, selfInitials }: { ext: string | null; selfInitials: string }) {
  const delays = [0, 0.12, 0.24, 0.36, 0.06, 0.3, 0.18, 0.42, 0.1]
  return (
    <div className="mt-[10px] flex items-center gap-[10px]">
      <span className="flex h-4 items-center gap-[2px]">
        {delays.map((d, i) => (
          <span
            key={i}
            className="inline-block w-[3px] animate-tk-lvl rounded-full"
            style={{ height: 16, animationDelay: `${d}s`, background: 'var(--brand-primary)' }}
          />
        ))}
      </span>
      <span
        className="inline-flex items-center gap-[6px] rounded-full py-[3px] pr-[9px] pl-[3px] text-[11px] font-medium"
        style={{ background: 'var(--tint-primary)', color: 'var(--brand-primary-text)' }}
      >
        <Avatar initials={selfInitials} size={18} />
        You have it
      </span>
      <span className="ml-auto flex-none text-[11px] text-lo">3CX ext {ext ?? '—'}</span>
    </div>
  )
}

function LiveMineCard({ card, covering, selfInitials, onSelect }: { card: CallFloorCard; covering: boolean; selfInitials: string; onSelect: () => void }) {
  const { thread } = card
  const call = thread.call!
  const elapsedS = useElapsedSeconds(thread.openedAt)
  return (
    <CardShell
      onSelect={onSelect}
      selected={false}
      radiusClass="rounded-card"
      className="shadow-sm"
      style={{ border: '1px solid var(--brand-primary-soft)', borderLeft: '3px solid var(--brand-primary)' }}
    >
      <div className="p-[11px]">
        <div className="flex items-center gap-[11px]">
          {/* Fix (visual findings, item 1): was `text-white` on plain
              `--brand-primary` — same under-3:1 defect as the ringing
              card's phone tile just above (fix round 2, finding 5) and the
              same under-4.5:1 defect as the outbound bubble / live-call
              strip. `--brand-primary-strong` + `--text-on-brand` is the
              same fix already proven for both of those. */}
          <span
            className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px]"
            style={{ background: 'var(--brand-primary-strong)', color: 'var(--text-on-brand)' }}
          >
            <Icon name="phone" size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-[7px]">
              <span className="truncate text-[13px] font-semibold text-hi">{thread.who}</span>
              <span className="inline-flex flex-none items-center gap-1 text-[9.5px] font-semibold tracking-[.08em] text-cc-blue">
                <span className="h-[6px] w-[6px] animate-tk-pulse rounded-full" style={{ background: 'var(--brand-primary)' }} />
                ON THE CALL
              </span>
            </div>
            <div className="mt-[2px] truncate text-[11.5px] text-mid">
              {thread.client ?? 'Unknown company'} · {thread.endpoint ?? 'unmapped handle'}
            </div>
          </div>
          <span
            data-testid={`call-floor-clock-${thread.id}`}
            className="flex-none font-mono text-[22px] leading-none text-hi"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatFloorClock(elapsedS)}
          </span>
        </div>
        <LiveMeterRow ext={call.ext} selfInitials={selfInitials} />
      </div>
    </CardShell>
  )
}

function LiveOtherCard({ card, covering, onSelect }: { card: CallFloorCard; covering: boolean; onSelect: () => void }) {
  const { thread } = card
  const elapsedS = useElapsedSeconds(thread.openedAt)
  // Binding decision 2: no per-technician presence field exists. `owner` is
  // the one fact ThreadSummary actually carries — when it is null (a live
  // call somehow unowned) this renders no name line at all, never a blank
  // slot where one would go.
  const ownerLine = thread.owner
    ? `${thread.owner.split(' ')[0]} is on it${thread.ticket ? ` · ${thread.ticket}` : thread.call!.ext ? ` · ext ${thread.call!.ext}` : ''}`
    : 'On this call'
  return (
    <CardShell onSelect={onSelect} selected={false} radiusClass="rounded-card" style={{ border: '1px solid var(--border-subtle)' }}>
      <div className="flex items-center gap-[11px] p-[11px]">
        {/* Fix round 2 (finding 2): channel tile, always voice on this floor
            — `channelTileStyle('voice')` gives the same tint/on-tint pair
            every other channel tile uses, replacing the ad hoc
            `--tint-primary` + `--brand-primary` pairing this card had
            instead (the latter isn't the token `--tint-primary` is paired
            with). */}
        <span
          className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px]"
          style={channelTileStyle('voice')}
        >
          <Icon name="phone" size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-[7px]">
            <span className="truncate text-[13px] font-semibold text-hi">{thread.who}</span>
            {thread.ticket ? (
              <span
                className="inline-flex flex-none items-center gap-1 rounded-[5px] px-[6px] py-px text-[10px] font-medium"
                style={{ background: 'var(--tint-accent)', color: 'var(--on-tint-accent)' }}
              >
                <Icon name="ticket" size={10} />
                {thread.ticket}
              </span>
            ) : null}
          </div>
          <div className="mt-[2px] flex items-center gap-[6px] truncate text-[11.5px] text-mid">
            {thread.owner ? <Avatar initials={initialsOf(thread.owner)} size={16} /> : null}
            {ownerLine}
          </div>
        </div>
        <span
          data-testid={`call-floor-clock-${thread.id}`}
          className="flex-none font-mono text-[16px] text-mid"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatFloorClock(elapsedS)}
        </span>
      </div>
    </CardShell>
  )
}

function SelectedCard({ card, covering, selfId, selfInitials, onSelect }: { card: CallFloorCard; covering: boolean; selfId: string | null; selfInitials: string; onSelect: () => void }) {
  const { thread } = card
  const elapsedS = useElapsedSeconds(thread.openedAt)
  const mine = thread.ownerId !== null && thread.ownerId === selfId
  return (
    <CardShell
      onSelect={onSelect}
      selected
      radiusClass="rounded-card"
      className="shadow-md"
      style={{ background: 'var(--brand-primary-soft)', border: '1px solid var(--brand-primary)', borderLeft: '3px solid var(--brand-primary)' }}
    >
      <div className="p-[11px]">
        <div className="flex items-center gap-[11px]">
          {/* Fix (visual findings, item 1): same `text-white` on plain
              `--brand-primary` defect as LiveMineCard's phone tile above —
              see that span's comment. */}
          <span
            className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px]"
            style={{ background: 'var(--brand-primary-strong)', color: 'var(--text-on-brand)' }}
          >
            <Icon name="phone" size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-[7px]">
              <span className="truncate text-[13px] font-semibold text-hi">{thread.who}</span>
              <span
                className="inline-flex flex-none items-center gap-1 text-[9.5px] font-semibold tracking-[.08em]"
                style={{ color: 'var(--brand-primary-text)' }}
              >
                <span className="h-[6px] w-[6px] animate-tk-pulse rounded-full" style={{ background: 'var(--brand-primary)' }} />
                OPEN HERE
              </span>
            </div>
            <div className="mt-[2px] truncate text-[11.5px]" style={{ color: 'var(--brand-primary-text)' }}>
              {thread.client ?? 'Unknown company'} · {thread.endpoint ?? 'unmapped handle'}
            </div>
          </div>
          <span
            data-testid={`call-floor-clock-${thread.id}`}
            className="flex-none font-mono text-[22px] leading-none text-hi"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatFloorClock(elapsedS)}
          </span>
        </div>
        {mine ? <LiveMeterRow ext={thread.call!.ext} selfInitials={selfInitials} /> : null}
      </div>
    </CardShell>
  )
}

/** `missed` and `unlogged` — README §2's "needs something" card, radius 10 rather than the others' 12. */
function NeedsSomethingCard({ card, onSelect }: { card: CallFloorCard; onSelect: () => void }) {
  const { thread, kind } = card
  const call = thread.call!
  const isMissed = kind === 'missed'
  return (
    <CardShell
      onSelect={onSelect}
      selected={false}
      radiusClass="rounded-[10px]"
      style={{ border: `1px solid var(${isMissed ? '--tint-danger' : '--tint-accent'})` }}
    >
      <div className="flex items-center gap-[10px] px-3 py-[9px]">
        {/* Fix round 2 (finding 6): --brand-accent-text, not --on-tint-accent
            — this icon sits directly on --surface-card (via CardShell), not
            on a --tint-accent fill. */}
        <span className="flex-none" style={isMissed ? undefined : { color: 'var(--brand-accent-text)' }}>
          <Icon name={isMissed ? 'phone-missed' : 'timer'} size={16} className={isMissed ? 'text-bad' : ''} />
        </span>
        <span data-testid={`call-floor-label-${thread.id}`} className="min-w-0 truncate text-[12px] font-medium text-hi">
          {isMissed
            ? `${thread.who} missed ${agoLabel(Date.now() - new Date(thread.lastActivity).getTime())}`
            : `${thread.who} · ${formatFloorClock(call.durationS ?? 0)} unlogged`}
        </span>
        {/* Binding decision 3: these wrapped at rail width during the design's own review. */}
        <span
          className="ml-auto inline-flex flex-none items-center gap-1 text-[11px] font-semibold text-cc-blue"
          style={{ whiteSpace: 'nowrap' }}
        >
          {isMissed ? (
            <>
              Call back
              <Icon name="arrow-right" size={12} />
            </>
          ) : (
            'Log time'
          )}
        </span>
      </div>
    </CardShell>
  )
}

export function CallFloor() {
  const threads = useStore((s) => s.threads)
  const activeId = useStore((s) => s.activeId)
  const selfId = useStore((s) => s.me?.id ?? null)
  const selfName = useStore((s) => s.me?.name ?? '')
  const covering = useStore((s) => s.covering)
  const floorOpen = useStore((s) => s.floorOpen)
  const svcVoice = useStore((s) => s.svc.voice)
  const set = useStore((s) => s.set)
  const selectThread = useStore((s) => s.selectThread)

  const cards = callFloorCards(threads, activeId, selfId)
  const counts = callFloorCounts(cards)
  const hasLiveOrRinging = counts.ringing > 0 || counts.live > 0
  // Binding decision 1: a ringing or live call always wins over the manual
  // preference — the floor auto-expands, it does not auto-COLLAPSE. Once
  // nothing is live, `floorOpen` (the user's own chevron choice) decides.
  const isOpen = floorOpen || hasLiveOrRinging
  const selfInitials = selfName ? initialsOf(selfName) : '?'

  function toggle(): void {
    set({ floorOpen: !floorOpen })
  }

  if (!isOpen) {
    return (
      <div
        data-testid="call-floor"
        data-floor-state="collapsed"
        className="flex-none border-b px-3 py-[11px]"
        style={{ background: 'var(--zone)', borderColor: 'var(--zone-border)' }}
      >
        <FloorHeaderRow summary={floorCollapsedLabel(counts)} onToggle={toggle} collapsed />
      </div>
    )
  }

  const showStatusLine = !hasLiveOrRinging
  const lastEndedAt = lastVoiceActivityAt(threads)
  const statusText = !svcVoice
    ? VOICE_DOWN_COPY
    : cards.length === 0
      ? `3CX connected · ${lastEndedAt ? `last call ended ${agoLabel(Date.now() - new Date(lastEndedAt).getTime())}` : 'no calls yet today'}`
      : '3CX connected · nothing ringing'

  return (
    <div
      data-testid="call-floor"
      data-floor-state={cards.length === 0 ? 'quiet' : hasLiveOrRinging ? 'active' : 'leftovers'}
      className="flex-none border-b p-3"
      style={{ background: 'var(--zone)', borderColor: 'var(--zone-border)' }}
    >
      <div className="pb-[10px]">
        <FloorHeaderRow summary={floorSummaryText(counts)} onToggle={toggle} collapsed={false} />
      </div>

      {cards.length === 0 ? (
        <div
          data-testid="call-floor-quiet"
          className="flex items-center gap-3 rounded-card p-3"
          style={{ background: 'var(--surface-card)', border: '1px solid var(--brand-primary-soft)' }}
        >
          {/*
            public/mascot-headset.png is a RESIZED COPY (214×312, ~95KB), not
            the vendored original. The source of truth is the full-resolution
            744×1082 PNG at docs/design-handoff/tossker-redesign/assets/
            mascot-headset.png, which stays untouched — do not restore the
            1MB file here thinking this was a mistake. Displayed at 52px
            tall, so 312px (~4x) stays crisp at any DPR without shipping a
            ~20x oversample on every quiet-empty-state render (fix round 1,
            task 3 review).
          */}
          {/*
            NO mix-blend-mode, deliberately, against the design doc.

            README §2's quiet-state row specifies `mix-blend-mode: multiply`.
            That is a light-theme technique for knocking a white box out from
            behind artwork, and multiply means `backdrop × source` — so on a
            dark card (either night theme's --surface-card) it multiplies every
            pixel toward black and renders the mascot as a dark silhouette.
            Andrew saw exactly that.

            No hex literals in this comment, deliberately: the guard in
            test/tokens.test.ts greps src/** for #rrggbb and does not exempt
            comments. That bluntness is correct — an exemption is where the
            rule starts rotting — so name tokens, not values.

            It is also unnecessary here: this PNG carries real alpha (47% of
            its pixels are fully transparent, all four corners are 0,0,0,0),
            so it composites correctly on all four themes with no blend mode
            at all. The design's instruction assumes an asset with a white
            background; ours does not have one.
          */}
          <img src="/mascot-headset.png" alt="" className="h-[52px] w-auto flex-none" />
          <div className="min-w-0">
            <div className="text-[12.5px] font-semibold text-hi">Nobody is on the phone</div>
            <div className="mt-[3px] text-[11.5px] leading-[1.5] text-mid">
              Three lines are open and the queue below is yours. Calls appear here the moment they ring.
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {cards.map((card) => {
            const key = card.thread.id
            switch (card.kind) {
              case 'ringing':
                return <RingingCard key={key} card={card} covering={covering} onSelect={() => selectThread(key)} />
              case 'live-mine':
                return <LiveMineCard key={key} card={card} covering={covering} selfInitials={selfInitials} onSelect={() => selectThread(key)} />
              case 'live-other':
                return <LiveOtherCard key={key} card={card} covering={covering} onSelect={() => selectThread(key)} />
              case 'selected':
                return (
                  <SelectedCard
                    key={key}
                    card={card}
                    covering={covering}
                    selfId={selfId}
                    selfInitials={selfInitials}
                    onSelect={() => selectThread(key)}
                  />
                )
              default:
                return <NeedsSomethingCard key={key} card={card} onSelect={() => selectThread(key)} />
            }
          })}
        </div>
      )}

      {showStatusLine ? (
        <div className="mt-[9px] flex items-center gap-2">
          <StatusDot ok={svcVoice} />
          <span data-testid="call-floor-status" className="text-[11px] text-mid">
            {statusText}
          </span>
        </div>
      ) : null}
    </div>
  )
}
